import { ensureSlash } from '@qdabra/forms-shared/urlHelpers';
import types from '../constants/parameterTypes';
import {
    paramUrl,
    paramXPath,
    paramDsName,
    paramName,
    paramNameXPath,
    paramOverwrite,
    paramAsync,
    paramDsNameProg,
    paramXPathProg,
    paramDsNameWs,
    paramDsUrl,
    paramClear,
    paramUniqueName,
} from '../constants/parameterNames';

import { ensureSharePointFilename } from '../../sharePoint/sharePointHelper';
import {
    clearFieldAndSetLinkAsync,
    getBase64ValuesForNode,
    verifyRequiredAttributesExist,
} from '../helpers/attachmentHelpers';
import { ERROR_INVALID_FILE_NAME_NODE } from '../constants/errors';
import { deleteNodesCollection } from '../helpers/qRules.node.helpers';

const requiredParameters = [
    {
        name: paramUrl,
        description: 'SharePoint Library URL',
    },
    {
        name: paramXPath,
        description: 'XPath to Picture(s) or Attachment(s)',
    },
];
const optionalParameters = [
    {
        name: paramDsName,
        description: 'Data Source Name',
        type: types.typeDataSource,
    },
    {
        name: paramName,
        description: 'Name for file (extension will be added by qRules)',
    },
    {
        name: paramNameXPath,
        description:
            'Relative XPath for name field -- not available for /async',
        type: types.typeXPath,
        dsParentName: paramDsName,
    },
    {
        name: paramOverwrite,
        description:
            'Overwrite existing file if name is specified (yes | no) -- default is no. Not available if /dsnamews is specified.',
        type: types.typeBoolean,
    },
    {
        name: paramAsync,
        description:
            'Filler Only - upload asynchronously to prevent blocking user while filling out form (yes | no) -- default is no. Ignored if command is used in browser.',
        type: types.typeBoolean,
    },
    {
        name: paramDsNameProg,
        description:
            'If uploading async, data source name for progress node - defaults to Main if not specified.',
        type: types.typeDataSource,
    },
    {
        name: paramXPathProg,
        description:
            'If uploading async, xpath for node to report progress to. Main data source assumed if /dsnameprog not specified.',
        type: types.typeXPath,
        dsParentName: paramDsNameProg,
    },
    {
        name: paramDsNameWs,
        description: 'Data Connection Name for Copy web service.',
        type: types.typeDataSource,
    },
    {
        name: paramDsUrl,
        description:
            'Site URL for Copy web service data connection. Use only if the data connection should have its url changed.',
    },
    {
        name: paramClear,
        description:
            '(yes | no)- indicates whether to clear the attachment field on successful upload. Default is yes.',
        type: types.typeBoolean,
    },
    {
        name: paramUniqueName,
        description:
            '(boolean, true by default) - Prepend date and time information to file names to provide unique names',
        type: types.typeBoolean,
    },
];

const errorFailedToCopyUnknownError =
    'Failed to save to SharePoint. The copy web service failed for unknown reasons.';
const errorFailedToCopy =
    'Failed to save to SharePoint. The copy web service returned an error. Error code "{0}".  Error message "{1}".';
const description =
    'Saves a file, a set of files, an image or a set of images to a SharePoint Document Library. Additional information can be found on http://www.infopathdev.com/files/folders/qrules/entry40104.aspx.';

function getTwoDigitDateFormat(value) {
    return value > 9 ? value : '0' + value;
}

function getValidFileName(shpAccess, fileName, fileExtension) {
    //TODO: Is correct way?
    fileName = ensureSharePointFilename(fileName);

    if (fileName.indexOf('.') < 0) {
        fileName += fileExtension;
    } else {
        var fileNameExt = fileName.split('.').pop().toLowerCase(),
            originalExt = fileExtension.replace('.', '').toLowerCase();

        if (fileNameExt !== originalExt) {
            fileName = fileName + '.' + originalExt;
        }
    }

    return fileName;
}

function generateFileName(
    cf,
    shpAccess,
    requestedFileName,
    fileNameXPath,
    fileName,
    fileExtension,
    fileNode,
    preserveName,
) {
    var toUse;

    //If filename or filename XPath is provided, use that.
    if (fileNameXPath) {
        var fileNameNode = cf.getValidNode(
            fileNode,
            fileNameXPath,
            ERROR_INVALID_FILE_NAME_NODE,
        );
        toUse = fileNameNode.value();
    } else if (requestedFileName) {
        toUse = requestedFileName;
    } else if (preserveName) {
        toUse = fileName;
    } else {
        var date = new Date(),
            year = date.getFullYear(),
            month = date.getMonth() + 1,
            dateValue = date.getDate(),
            hours = date.getHours(),
            minutes = date.getMinutes(),
            seconds = date.getSeconds(),
            milliseconds = date.getMilliseconds(); //TODO: fffff format implementation

        toUse = String.format(
            '{0}{1}{1}{3}{4}{5}{6}_{7}',
            year,
            getTwoDigitDateFormat(month),
            getTwoDigitDateFormat(dateValue),
            getTwoDigitDateFormat(hours),
            getTwoDigitDateFormat(minutes),
            getTwoDigitDateFormat(seconds),
            getTwoDigitDateFormat(milliseconds),
            fileName,
        );
    }

    return getValidFileName(shpAccess, toUse, fileExtension);
}

function processResult(copyDs, fileUrl, fileName) {
    var error = copyDs.selectSingle('//@ErrorCode');
    if (!error) {
        throw new Error(errorFailedToCopyUnknownError);
    }

    var errorValue = error.value();
    if (errorValue === 'Success') {
        return {
            result: true,
            url: fileUrl + fileName,
        };
    }

    var errorMsgNode = copyDs.selectSingle('//@ErrorMessage'),
        message =
            !errorMsgNode && !errorMsgNode.value()
                ? 'Not specified'
                : errorMsgNode.value();

    throw new Error(String.format(errorFailedToCopy, errorValue, message));
}

function SaveToSharePoint(params) {
    function saveWithDataConnection(
        cf,
        fileUrl,
        fileName,
        base64,
        copyServiceName,
        copyServiceUrl,
    ) {
        cf.verifyDataConnectionExists(copyServiceName);

        var ds = params.dataSources,
            dc = params.dataConnections,
            copyDs = ds.getDom(copyServiceName),
            source = copyDs.selectSingle(
                "//*[starts-with(local-name(), 'SourceUrl')]",
            ),
            destination = copyDs.selectSingle(
                "//*[starts-with(local-name(), 'string')]",
            ),
            stream = copyDs.selectSingle(
                "//*[starts-with(local-name(), 'Stream')]",
            ),
            fields = copyDs.selectNodes(
                "//*[starts-with(local-name(), 'FieldInformation')]",
            );

        deleteNodesCollection(fields);

        return source
            .setValueAsync(' ')
            .then(function () {
                return destination.setValueAsync(fileUrl + fileName);
            })
            .then(function () {
                return stream.setValueAsync(base64);
            })
            .then(function () {
                var ws = dc.get(copyServiceName);

                if (copyServiceUrl) {
                    if (!copyServiceUrl.endsWith('/')) {
                        copyServiceUrl += '/';
                    }

                    copyServiceUrl += '_vti_bin/Copy.asmx';

                    ws.setUrl(copyServiceUrl);
                }

                return ws.executeAsync();
            })
            .then(function () {
                return processResult(copyDs, fileUrl, fileName);
            })
            .catch(function () {
                return processResult(copyDs, fileUrl, fileName);
            });
    }

    async function saveFile(
        cf,
        copyServiceName,
        fileUrl,
        fileName,
        fileNameToUse,
        base64,
        overwrite,
        copyServiceUrl,
    ) {
        const url = fileUrl + fileNameToUse;

        try {
            if (copyServiceName) {
                return await saveWithDataConnection(
                    cf,
                    fileUrl,
                    fileNameToUse,
                    base64,
                    copyServiceName,
                    copyServiceUrl,
                );
            }

            const isBase64 = true;
            const data = await params.shpAccess.submitToLibraryAsync(
                fileUrl,
                fileNameToUse,
                base64,
                overwrite,
                isBase64,
            );

            return {
                result: data.success,
                url,
                fileName,
                message: data.message,
                response: data.resultBody,
            };
        } catch (error) {
            return {
                result: false,
                url,
                fileName,
                message:
                    error.userDisplayMessage ||
                    error.message ||
                    'Unspecified error',
            };
        }
    }

    // (String, Boolean, Boolean, String) -> Promise[Boolean]
    async function overwriteCheck(
        copyServiceName,
        overwriteExisting,
        isSpecificFileName,
        fullPath,
    ) {
        try {
            if (copyServiceName || overwriteExisting || !isSpecificFileName) {
                return true;
            }

            return !(await params.shpAccess.fileExistsAsync(fullPath));
        } catch (e) {
            console.error(e);

            throw new Error(
                `Encountered an error checking whether ${fullPath} already exists: ${e.message}`,
            );
        }
    }

    async function saveFileToSharePoint(
        cf,
        {
            url,
            name: requestedFileName,
            fileNameXPath,
            overwrite,
            preserveName,
            copyServiceName,
            copyServiceUrl,
        },
        fileNode,
    ) {
        const properties = getBase64ValuesForNode(fileNode);
        const fileName = properties.fileName;
        const fileExtension = properties.fileExtension;
        const shpAccess = params.shpAccess;
        //folderUrl = fileUrl, // Input is folder url, output is file url
        const base64 = properties.base64;
        const fileNameToUse = generateFileName(
            cf,
            shpAccess,
            requestedFileName,
            fileNameXPath,
            fileName,
            fileExtension,
            fileNode,
            preserveName,
        );

        const isSpecificFileName =
            requestedFileName || fileNameXPath || preserveName;
        const fullPath = url + fileNameToUse;

        // Overwrite defaults to no. However, if user is not specifying file name, the file name will be unique and we don't need to check for existing.
        const proceed = await overwriteCheck(
            copyServiceName,
            overwrite,
            isSpecificFileName,
            fullPath,
        );

        if (!proceed) {
            return {
                result: false,
                alreadyExists: true,
                url: fullPath,
                fileName: fileNameToUse,
            };
        }

        return await saveFile(
            cf,
            copyServiceName,
            url,
            fileName,
            fileNameToUse,
            base64,
            overwrite,
            copyServiceUrl,
        );
    }

    function saveFailReason(result) {
        if (result.alreadyExists) {
            return 'The file already exists.';
        }

        if (result.message) {
            return result.message;
        }

        return 'Unspecified error.';
    }

    const processFileNode = async (cf, settings, node) => {
        const { clear } = settings;

        verifyRequiredAttributesExist(node);

        const base64Value = node.value();

        if (!base64Value) {
            return false;
        }

        const data = await saveFileToSharePoint(cf, settings, node);

        if (data.result) {
            await clearFieldAndSetLinkAsync(
                node,
                data.fileName,
                data.url,
                clear,
            );

            return true;
        }

        const err = `Failed to save to SharePoint. URL = '${
            data.url
        }'. ${saveFailReason(data)}`;

        throw new Error(err);
    };

    async function saveToSharepoint(cf, settings) {
        const { dsname, xpath } = settings;

        const dom = cf.getDataSource(dsname);

        const nodes = cf.getValidNodeSet(dom, xpath);

        let count = 0;

        for (const node of nodes) {
            const saved = await processFileNode(cf, settings, node);

            if (saved) {
                count += 1;
            }
        }

        return count;
    }

    async function executeAsync() {
        //TODO: IsRunningAsync implementation required ?
        const cf = params.commonFunction;
        // removed unused async option
        const settings = {
            url: ensureSlash(cf.getParamValue(paramUrl)),
            dsname: cf.getParamValue(paramDsName),
            xpath: cf.getParamValue(paramXPath),
            name: cf.getParamValue(paramName),
            overwrite: cf.getBoolParamValue(paramOverwrite),
            dsnameprog: cf.getParamValue(paramDsNameProg),
            xpathprog: cf.getParamValue(paramXPathProg),
            copyServiceName: '', //cf.getParamValue(paramDsNameWs),//TODO:Uncomment after fix for copy.asmx
            copyServiceUrl: cf.getParamValue(paramDsUrl),
            fileNameXPath: cf.getParamValue(paramNameXPath),
            clear: cf.getBoolParamValue(paramClear, true),
            preserveName: !cf.getBoolParamValue(paramUniqueName, true),
        };

        const savedFileCount = await saveToSharepoint(cf, settings);

        return {
            result: savedFileCount,
            success: savedFileCount > 0,
        };
    }

    return {
        description,
        executeAsync,
        optionalParameters,
        requiredParameters,
    };
}

export default SaveToSharePoint;
